// recovery-batch-governance-scope.ts
//
// Orchestrator recovery (3.40.0, T3.6 / N7): no road from a recovery ticket
// to a gate input. The general scope guard (proposal-scope-guard.ts) already
// refuses the verification profile, PLAN.md, `.claude/state`, CI/hooks and
// unbounded scopes; a recovery batch is additionally refused — UNCODED, i.e.
// escalated, never a counted retry — when any ticket it creates or amends
// would be scoped over the BLUEPRINT (G0's input: only a person changes it)
// or over a path leaving the worktree (`..` segment, absolute, `~`). Runs with
// the governance prescan, before any fixable check (audit F-1) — in the done
// path BEFORE the counted `proposed_tickets` check, and again inside apply
// (batchGovernanceRefusal is the one entry for both).
//
// Security audit M-2: the BLUEPRINT is also the path the mission bound at
// init (`blueprint-binding.json`'s `source_path`, passed in by the caller),
// matched case-insensitively with `\` read as `/` — a case-insensitive
// filesystem writes `blueprint.md` into `BLUEPRINT.md`.
//
// Security audit M-3: also refused for a recovery batch — (a) an acceptance
// that hands an open decision to the worker "theo G0" (plan tickets get this
// check in the done path; a T3 recovery re-plans without being a plan), and
// (b) a scope over the repo's control files: the root `.gitignore` /
// `.gitattributes`, any pattern naming one directly, and `.avatar/plans`
// (the phase docs workers read as instructions).
//
// Re-audit M-R1: `.avatar/plans` is matched segment by segment, not with one
// probe file — `.avatar/plans/bootstrap.md`, `.avatar/plans/*.md` and
// `.avatar/*/bootstrap.md` all reach a phase doc without matching `any-file`.
// A `**` segment spans folders, so that pattern is intersected exactly with
// the plan docs the pack writes, flat `.avatar/plans/*.md` (review G3 M2:
// `**/*.test.ts` stays usable; re-audit M-R3: no sample names to slip past).
// Re-audit M-R2: `?` is refused outright — the verifier's matcher leaves it a
// regex quantifier (`/?` makes a slash optional), so no segment walk holds.

import { posix } from "node:path";
import type { AutopilotResult } from "./autopilot-result-schema.js";
import {
  type ControllerLedgerState,
  controllerRecoveryOrigin,
} from "./controller-redirect-decision-ticket.js";
import { globsIntersect } from "./glob-pattern-intersection.js";
import { governanceRefusalInBatch } from "./proposal-batch-governance-prescan.js";
import { quoteWorkerValue } from "./proposal-change-types.js";
import { matchesGlob, pathAllowed } from "./result-verifier-scope-checks.js";
import type { Ticket } from "./schemas/ticket-schema.js";
import { findPlanAcceptanceDelegatingToWorker } from "./worker-decisions-g0-delegation-claim-guard.js";

const DEFAULT_BLUEPRINT_PATHS: readonly string[] = [".avatar/BLUEPRINT.md", "BLUEPRINT.md"];

const slashed = (p: string) => p.trim().replace(/\\/g, "/");

function blueprintPaths(bound: string | undefined): string[] {
  const all = bound === undefined ? DEFAULT_BLUEPRINT_PATHS : [...DEFAULT_BLUEPRINT_PATHS, bound];
  return all.map((p) => posix.normalize(slashed(p)).toLowerCase());
}

/** Probe paths of the repo control files (M-3 b), lowercase like the patterns they are matched with. */
const CONTROL_FILE_PROBES: readonly string[] = [
  ".gitignore",
  ".gitattributes",
  ".avatar/plans/any-file",
];
const CONTROL_FILE_NAMES: readonly string[] = [".gitignore", ".gitattributes"];
const PLAN_DOCS_DIR: readonly string[] = [".avatar", "plans"];
const PLAN_DOCS_GLOB = ".avatar/plans/*.md";

/**
 * M-R1: can `pattern` match some path at or beneath `.avatar/plans`? Walked
 * segment by segment: each must match the directory name, and a pattern still
 * going once both names are consumed matches inside the directory. A segment
 * holding `**` spans folders, so the whole pattern is intersected with the
 * flat plan docs instead (M-R3). A pattern ending earlier is the
 * directory-prefix form, which the probes check.
 */
function reachesPlanDocs(pattern: string): boolean {
  const segments = pattern.split("/");
  for (const [dirIndex, dirName] of PLAN_DOCS_DIR.entries()) {
    const segment = segments[dirIndex];
    if (segment === undefined) return false;
    if (segment.includes("**")) return globsIntersect(pattern, PLAN_DOCS_GLOB);
    if (!matchesGlob(segment, dirName)) return false;
  }
  return true;
}

function outOfRecoveryReach(pattern: string, blueprints: string[]): boolean {
  const p = slashed(pattern);
  if (/^([/~]|[A-Za-z]:)/.test(p) || p.split("/").includes("..") || p.includes("?")) return true;
  const lower = p.toLowerCase();
  if (CONTROL_FILE_NAMES.includes(lower.split("/").at(-1) ?? "")) return true;
  try {
    if (reachesPlanDocs(lower)) return true;
    return [...blueprints, ...CONTROL_FILE_PROBES].some((probe) => pathAllowed(probe, [lower]));
  } catch {
    return true; // a pattern the matcher cannot compile is never in reach (fail closed)
  }
}

/** Re-audit L-new: an amend moving an existing ticket's instruction files (phase_path / plan_path). */
function retargetedInstructionPath(
  change: AutopilotResult["proposed_changes"][number],
  tickets: readonly Ticket[],
): string | undefined {
  const patch = change.content as Partial<Record<"phase_path" | "plan_path", unknown>> | undefined;
  const target = tickets.find((t) => t.id === change.target);
  if (typeof patch !== "object" || patch === null) return undefined;
  return (["phase_path", "plan_path"] as const).find(
    (field) => field in patch && patch[field] !== target?.[field],
  );
}

function allowedPathsOf(change: AutopilotResult["proposed_changes"][number]): string[] {
  const content = change.content as { allowed_paths?: unknown } | undefined;
  const paths = typeof content === "object" && content !== null ? content.allowed_paths : undefined;
  return Array.isArray(paths) ? paths.filter((p): p is string => typeof p === "string") : [];
}

export interface BatchGovernanceInput {
  store: { state: ControllerLedgerState };
  sourceTicketId?: string;
  proposedChanges: AutopilotResult["proposed_changes"];
  /** M-2: `source_path` of the mission's blueprint binding, when one exists. */
  blueprintPath?: string;
  /** M-3 (a): masks worker text quoted in the delegation reason; undefined ⇒ patterns only. */
  secretValues?: readonly string[];
}

/** Audit F-1: every UNCODED refusal of a batch — the general prescan, then the recovery scope. */
export function batchGovernanceRefusal(
  tickets: Ticket[],
  input: BatchGovernanceInput,
): string | undefined {
  return (
    governanceRefusalInBatch(tickets, input.proposedChanges) ??
    recoveryGovernanceRefusal(tickets, input)
  );
}

export function recoveryGovernanceRefusal(
  tickets: readonly Ticket[],
  input: BatchGovernanceInput,
): string | undefined {
  const source = tickets.find((t) => t.id === input.sourceTicketId);
  if (source === undefined || controllerRecoveryOrigin(source, input.store.state) === undefined) {
    return undefined;
  }
  const blueprints = blueprintPaths(input.blueprintPath);
  for (const [index, change] of input.proposedChanges.entries()) {
    if (change.op !== "create" && change.op !== "amend") continue;
    const retargeted =
      change.op === "amend" ? retargetedInstructionPath(change, tickets) : undefined;
    if (retargeted !== undefined) {
      return (
        `proposed_changes[${index}] (amend ${quoteWorkerValue(change.target)}): ticket gỡ kẹt đổi ` +
        `${retargeted} của ticket có sẵn — phase_path/plan_path chỉ người đổi (redirect); trả needs_decision.`
      );
    }
    const bad = allowedPathsOf(change).find((p) => outOfRecoveryReach(p, blueprints));
    if (bad !== undefined) {
      return (
        `proposed_changes[${index}] (${change.op} ${quoteWorkerValue(change.target)}): ticket gỡ kẹt ` +
        `khai allowed_paths ${quoteWorkerValue(bad)} chạm BLUEPRINT, .gitignore/.gitattributes, ` +
        ".avatar/plans, ra ngoài worktree hoặc dùng `?` — chỉ người đổi BLUEPRINT/gate/config; trả needs_decision."
      );
    }
  }
  return findPlanAcceptanceDelegatingToWorker({
    proposedChanges: input.proposedChanges,
    ...(input.secretValues === undefined ? {} : { secretValues: input.secretValues }),
  });
}
